import { useAppConfigContext } from "@/context/AppConfig";
import { useReportContext } from "@/context/ReportContext";
import { useReportRating } from "@/reports/reportRating/useReportRating";
import { Rating } from "@/theme/components";
import { Box, Button, Stack, Text, TextArea, styled } from "@/theme/index";
import { useRef, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
const StyledRating = styled(Rating)(
  ({ theme }) => `
    margin-top: 0px;
    .MuiRating-icon {
      color: ${theme.palette.grey[600]};
      svg {
        width: 22px;
        height: 30px;
        margin-right: ${theme.spacing(1.5)};
      }
    }
    .MuiRating-iconEmpty {
      color: ${theme.palette.grey[500]};
    }

`,
);

type FormValues = {
  comments: string;
};

export function ReportRating({
  rating,
  report_type,
}: {
  rating: number | null;
  report_type: string;
}) {
  const { trackEventGA4 } = useAppConfigContext();

  const { permalink } = useReportContext();
  const [rate, setRate] = useState({ rating, rateChanged: false });
  const prevRatingRef = useRef<number | null>(rating);
  const reportRating = useReportRating(permalink);
  const { register, handleSubmit } = useForm<FormValues>();
  const onSubmit: SubmitHandler<FormValues> = (data) => {
    reportRating.mutate(
      {
        permalink,
        rate: `${rate.rating}`,
        comments: data.comments,
      },
      {
        onSuccess: () => {
          setRate((prevState) => ({ ...prevState, rateChanged: false }));
        },
      },
    );
  };
  if (prevRatingRef.current !== rating) {
    setRate((prevState) => ({ ...prevState, rating }));
    prevRatingRef.current = rating;
  }
  return (
    <Box
      py={2.5}
      px={2}
      marginBottom={2.5}
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: { xs: 0, md: "4px" },
        backgroundColor: (theme) => theme.palette.background.paper,
      }}
      id="report-rating"
    >
      <Stack
        sx={{
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { sm: "center" },
          justifyContent: { sm: "space-between" },
          width: "100%",
        }}
        spacing={{ xs: 2, sm: 0 }}
      >
        <Text variant="body1">
          {rate.rating && !rate.rateChanged ? (
            <>
              <strong>Thank you!</strong> You rated this report as
            </>
          ) : (
            "How would you rate this report?"
          )}
        </Text>
        <StyledRating
          value={rate.rating}
          onChange={(_event, newValue) => {
            if (newValue !== null) {
              trackEventGA4({
                event_name: "report_rating",
                rating: newValue,
                report_type: report_type,
              });
              setRate({ rating: newValue, rateChanged: true });
            }
          }}
          className="rating-input-component"
        />
      </Stack>
      {rate.rateChanged && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack
            sx={{
              alignItems: { xs: "flex-start", sm: "flex-end" },
            }}
            direction="column"
            spacing={2}
            mt={2}
          >
            <TextArea
              placeholder="Add comments (optional)"
              {...register("comments")}
              fullWidth
            />
            <Button
              type="submit"
              variant="contained"
              color="success"
              disabled={reportRating.isLoading}
            >
              Submit your rating
            </Button>
          </Stack>
        </form>
      )}
    </Box>
  );
}
